import React from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { z } from 'zod';

import Switch from 'components/Switch/Switch';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';

import { templateStudioSchema } from '../../../validation-schema';
import { TemplateStudioPromotionLayoutSelect } from '../Components/PromotionLayoutSelect';
import { ThemesFormItem } from '../Components/ThemesFormItem';

export const TemplateStudioPromotionForm: React.FC = () => {
  const { control } = useFormContext<z.infer<typeof templateStudioSchema>>();
  const { supportedThemesForPromotions } = useTemplateStudioState();
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const isCarouselV2Enabled = !!featureFlags?.[EFeatureFlag.STORE_CAROUSEL_V2];
  const isCarouselView = useWatch({
    control,
    name: 'promotion.isCarouselView'
  });

  if (!supportedThemesForPromotions) return null;

  return (
    <>
      <FormField
        control={control}
        name="promotion.themes"
        render={({ field }) => (
          <ThemesFormItem
            onChange={field.onChange}
            value={field.value || []}
            supportedThemes={supportedThemesForPromotions}
            themesSelectDataTestId="TemplatesStudio-Promotion-themes"
          />
        )}
      />
      {isCarouselV2Enabled && (
        <>
          <FormField
            control={control}
            name="promotion.isCarouselView"
            render={({ field }) => (
              <Switch
                key={field.value ? 'on' : 'off'}
                label="Gallery View"
                className="h-[27px] align-middle"
                textAlignment="left"
                defaultChecked={field.value}
                checked={field.value}
                onCheckedChange={field.onChange}
                data-testid="TemplatesStudio-Promotion-isCarouselView"
              />
            )}
          />
          {isCarouselView && (
            <FormField
              control={control}
              name="promotion.slidesPerViewDesktop"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <TemplateStudioPromotionLayoutSelect
                      value={field.value ?? 3}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </>
      )}
    </>
  );
};
